import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  Router,
  ActivationEnd,
  ActivatedRouteSnapshot,
  Data,
  EventType,
} from "@angular/router";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { Subscription, filter } from "rxjs";
import { ApplicationMessageTypeEnum, PartyTypeEnum } from "src/domain/client";
import { CookieKeys } from "src/domain/constants";
import { ChannelRequestService } from "src/services/channel-request.service";
import { CookieService } from "src/services/cookie.service";
import { ModelAndDealerService } from "src/services/model-and-dealer.service";
import { ToastService } from "src/services/toast.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "vw-top-banner",
  templateUrl: "./top-banner.component.html",
  imports: [TranslateModule],
})
export class TopBannerComponent implements OnInit, OnDestroy {
  ApplicationMessageTypeEnum = ApplicationMessageTypeEnum;

  private routeSubscription?: Subscription;

  constructor(
    public toastService: ToastService,
    private modelAndDealerService: ModelAndDealerService,
    private router: Router,
    private translateService: TranslateService,
    private cookieService: CookieService,
    public channelRequestService: ChannelRequestService,
  ) {}

  ngOnInit(): void {
    this.initRouteSubscription();

    this.handleActiveRouteSnapshot(
      this.router.routerState.snapshot.root,
      (): boolean =>
        !this.channelRequestService.channelRequest ||
        !this.channelRequestService.channelRequest.shoppingCart?.vehicle ||
        !this.channelRequestService.channelRequest.parties?.find(
          (_) => _.partyTypeName === PartyTypeEnum.Dealer,
        ),
    );

    this.toastService.getApplicationMessages();
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }

  close(topBannerMessageId: string): void {
    this.toastService.deleteTopBannerMessage(topBannerMessageId);
  }

  closeApplicationMessage(applicationMessageId: number): void {
    this.toastService.closeApplicationMessage(applicationMessageId);
  }

  get showBackdrop(): boolean {
    return this.toastService.topBannerMessages().some((m) => m.backdrop);
  }

  flattenRouteData(route: ActivatedRouteSnapshot): Data;
  flattenRouteData(route: ActivatedRouteSnapshot, data: Data): void;
  flattenRouteData(route: ActivatedRouteSnapshot, data?: Data): Data | void {
    if (data == null) {
      const newData = Object.assign({}, route.data);
      route.children.forEach((child) => {
        this.flattenRouteData(child, newData);
      });
      return newData;
    }
    Object.assign(data, route.data);
    route.children.forEach((child) => {
      this.flattenRouteData(child, data);
    });
  }

  handleActiveRouteSnapshot(
    snapshot: ActivatedRouteSnapshot,
    additionalConditionCallback: () => boolean,
  ) {
    const fullRouteData = this.flattenRouteData(snapshot);
    this.checkIfCarOrNoDealerWarningNeeded(
      fullRouteData,
      additionalConditionCallback,
    );
  }

  //////////

  private initRouteSubscription(): void {
    this.routeSubscription = this.router.events
      .pipe(
        filter(
          (ev): ev is ActivationEnd => ev.type === EventType.ActivationEnd,
        ),
      )
      .subscribe((event) => {
        this.handleActiveRouteSnapshot(
          event.snapshot,
          () =>
            !this.modelAndDealerService.selectedDealer ||
            !this.channelRequestService.channelRequest ||
            !this.channelRequestService.channelRequest.shoppingCart ||
            !this.channelRequestService.channelRequest.shoppingCart.vehicle,
        );
      });
  }

  private checkIfCarOrNoDealerWarningNeeded(
    routeData: Data,
    additionalConditionCallback: () => boolean,
  ): void {
    if (
      !routeData[Symbol.for("TBO:HIDE_CAR_OR_NO_DEALER_WARNING")] &&
      !this.cookieService.getCookie(CookieKeys.noCarOrDealerWarningClosed) &&
      additionalConditionCallback()
    ) {
      this.toastService.createTopBannerMessage(
        this.translateService.instant("topBannerMessages.noCarOrDealer.title"),
        this.translateService.instant("topBannerMessages.noCarOrDealer.text"),
        true,
        () => {
          this.cookieService.setCookie(
            CookieKeys.noCarOrDealerWarningClosed,
            "true",
            14,
          );
        },
      );
    }
  }
}
